import {
	createAsyncThunk,
	createSelector,
	createSlice,
} from "@reduxjs/toolkit";
import {
	getObservations,
	getObservationIDs,
	getObservationById,
	tagObservationById,
	unTagObservationById,
	acceptObservation,
	archiveObservation,
} from "./api";
import { chunkArray, unChunkDataArray } from "../../chunk_array";
import { allowUpdate } from "../../app/store";

const initialState = {
	byId: {}, // A map of observations by id
	pipelines: ["azure-schedule", "manual", "sp3"],
	solverStatuses: [
		"success",
		"no_astrometry_match",
		"not_enough_stars",
		"no_stars_left_after_filtering",
		"no_satellites_no_astrometry_match",
		"no_satellites",
		"crash",
		"skip",
		"none",
	],
	taskTypes: ["track", "balanced_speed", "calibrate", "spiral_search"],
	tagDescriptions: {
		viewed: {
			name: "viewed",
			colorCode: "#cbe8ba",
			key: "Q",
			manual: true,
		},
		stars: {
			name: "stars",
			colorCode: "#e692f8",
			key: "W",
			manual: true,
		},
		satellite: {
			name: "satellite",
			colorCode: "#f6a2bf",
			key: "E",
			manual: true,
		},
		other_satellite: {
			name: "other_satellite",
			colorCode: "#ffd8ad",
			key: "F",
			manual: true,
		},
		glint: {
			name: "glint",
			colorCode: "#c0fefa",
			key: "T",
			manual: true,
		},
		wind: {
			name: "wind",
			colorCode: "#a8c6fa",
			key: "Y",
			manual: true,
		},
		cloud_edge: {
			name: "cloud_edge",
			colorCode: "#d4e3fc",
			key: "U",
			manual: true,
		},
		moon: {
			name: "moon",
			colorCode: "#fefac0",
			key: "I",
			manual: true,
		},
		solve: {
			name: "solve",
			colorCode: "#cbe8ba",
			manual: false,
		},
		no_allsky: {
			name: "no_allsky",
			colorCode: "#cbe8ba",
			manual: false,
		},
		"solve-satellites-count": {
			name: "solve-satellites-count",
			colorCode: "#cbe8ba",
			manual: false,
		},
		low_confidence: {
			name: "low_confidence",
			colorCode: "#e8dfba",
			manual: false,
		},
		too_many_events: {
			name: "too_many_events",
			colorCode: "#cbe8ba",
			manual: false,
		},
	},
	matchesByCtx: {
		// root context powering the observations list view
		root: {
			_loadStatus: "idle", // Information about the loading status of a call
			_errorMessage: undefined,
			ids: [], // IDs. Details for each are in `byId`.
			page: {}, // Pagination information
		},
		/*
			Example of other contexts
			"collection/123": {
				ids: [],
				page: {}, // Pagination information
			},
		*/
	},
	tagApplicationByObservationId: {},
	acceptanceByObservationId: {},
	// `ids` contain a single list of IDs that match some or other filter.
	// Used by the UI to, for example, track navigation of observations
	// through an arbitrary filtered list, such as "last week's failed observations"
	// in a Previous / Next step-through manner. Exists separately from `matchesByCtx`
	// as we don't have to support different contexts or pagination.
	ids: {
		_loadStatus: "idle", // Information about the loading status of a call
		_errorMessage: undefined,
		ids: [], // IDs. Details for each are in `byId`.
	},
	archiving: {}, // RSOS that will be archived on submission

	/*
		 Example of _loadStatus pattern for a single object
		 byId: {
				'1': {
					id: '1',
					_loadStatus: 'idle',
				}
			}
	 */
};

/**
 * Thunk to perform async logic to fetch the latest observations
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchObservations = createAsyncThunk(
	"observations/fetch",
	/**
	 *
	 * @param filter the filters to apply to the observations search
	 * @param page Pagination information
	 * @param context The context from where the query is being made.  If its from the observations list then root otherwise examples could be `collection/123`
	 * @returns {Promise<*>}
	 */
	async ({ filter, page, context }) => {
		const response = await getObservations({
			filter,
			page,
		});
		if (context) {
			response.context = context;
		}
		return response;
	},
);

/**
 * Thunk to fetch a list of observation IDs matching the given filter.
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchObservationIDs = createAsyncThunk(
	"observations/fetch-ids-only",
	/**
	 * @param filter the filters to apply to the observations search
	 * @returns {Promise<*>}
	 */
	async ({ filter }) => {
		return getObservationIDs({ filter });
	},
);

/**
 * Thunk to perform async logic to delete priority lists by their IDs
 * @type {AsyncThunk<*, void, {}>}
 */
export const archiveObservationByID = createAsyncThunk(
	"observations/archive",
	async (id, { rejectWithValue }) => {
		try {
			return await archiveObservation(id);
		} catch (error) {
			throw rejectWithValue(error.message);
		}
	},
);

/**
 * Thunk to perform async logic to fetch multiple observations by their ids.
 * We specifically separate this from the general version as the query string becomes too large, and
 * we need to split up the calls.
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchObservationsByIds = createAsyncThunk(
	"observations/ids/fetch",
	async (observationIds) => {
		let response = undefined;
		if (observationIds && observationIds.length > 0) {
			// We need to split up calls where the query string gets too long.
			const chunks = chunkArray(observationIds, 50);
			const promises = [];
			chunks.map((subChunk) => {
				promises.push(
					getObservations({
						filter: {
							observation_ids: subChunk,
						},
					}),
				);
			});
			return Promise.all(promises);
		}
		return response;
	},
);

/**
 * Thunk to perform async logic to fetch an observation by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchObservationById = createAsyncThunk(
	"observation/fetch",
	async (observationId) => {
		return await getObservationById(observationId);
	},
);

/**
 * Thunk to perform async logic to un/tag an observation
 * @type {AsyncThunk<*, void, {}>}
 */
export const toggleTag = createAsyncThunk(
	"observation/tags/apply",
	async (request, { getState }) => {
		// Here we add some toggle logic so that if the value is not set we look it up from state
		const byId = getState().observations.byId;
		// Start off by assume we are setting the tag
		// Check if there is a tag in state already and if so invert the value
		request.value = true;
		request._etag = byId[request.observationId]?._etag;

		if (byId[request.observationId]) {
			// let tags = byId[request.observationId]?.tags.map((tagId) => byId[tagId])
			let matchingTag = byId[request.observationId]?.tags?.find(
				(tag) => tag.name == request.name && tag.value !== false,
			);
			if (!matchingTag) {
				request.value = true;
				const response = await tagObservationById(request);
				return response;
			} else {
				request.value = false;
				const response = await unTagObservationById(request);
				// No response returned, return recording from state with
				// tags removed (for compatability, ideally re-work pattern
				// at a higher level)
				// return response;
				// Expects: { recording, derived_files, _at}
				return null
			}
		}
	},
);

/**
 * Thunk to perform async logic to accept or decline an observation (indicating if there is valid data)
 * @type {AsyncThunk<*, void, {}>}
 */
export const observationAcceptance = createAsyncThunk(
	"observation/acceptance",
	async (request) => {
		const response = await acceptObservation(request);
		return response;
	},
);

/**
 * Observations reducer
 */
export const observationSlice = createSlice({
	name: "observations",
	initialState,
	// The `reducers` field lets us define reducers and generate associated actions
	reducers: {
		clearMatches: (state, action) => {
			state.matchesByCtx[action?.payload || "root"].ids = [];
		},
	},
	// The `extraReducers` field lets the slice handle actions defined elsewhere,
	// including actions generated by createAsyncThunk or in other slices.
	extraReducers: (builder) => {
		builder
			.addCase(fetchObservations.pending, (state, action) => {
				let context = action?.meta?.arg?.context || "root";
				if (!state.matchesByCtx[context]) {
					state.matchesByCtx[context] = {
						ids: [],
						page: {},
					};
				}
				state.matchesByCtx[context]._loadStatus = "loading";
			})
			.addCase(fetchObservations.fulfilled, (state, action) => {
				let context = action?.meta?.arg?.context || "root";
				if (state.matchesByCtx[context]) {
					state.matchesByCtx[context]._loadStatus = "idle";
					if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
						// Don't reset if we are paging
						if (action?.payload?.page?.start_index === 0) {
							state.matchesByCtx[context].ids = [];
						}
						action?.payload?.data?.map((observation) => {
							// Add the observations to state by id (this can include ids not retrieved in this call)
							state.byId[observation.id] = {
								...observation,
								_at: action.payload.at,
							};
							// add the ids of the observations that match from this query
							if (
								state.matchesByCtx[context].ids.indexOf(observation.id) === -1
							) {
								state.matchesByCtx[context].ids.push(observation.id);
							}
						});
						state.matchesByCtx[context].page = action.payload.page;
						state.matchesByCtx[context].at = action.payload.at;
					}
				}
			})
			.addCase(fetchObservations.rejected, (state, action) => {
				let context = action?.meta?.arg?.context || "root";
				state.matchesByCtx[context]._loadStatus = "rejected";
				state.matchesByCtx[context]._errorMessage = action?.error?.message;
			})
			.addCase(fetchObservationById.pending, (state, action) => {
				if (!state.byId[action.meta.arg]) {
					state.byId[action.meta.arg] = {};
				}
				state.byId[action.meta.arg]._loadStatus = "loading";
			})
			.addCase(fetchObservationById.fulfilled, (state, action) => {
				if (action?.payload?.recording?.id) {
					if (
						allowUpdate(
							state.byId[action.payload.recording.id]
								? state.byId[action.payload.recording.id]._at
								: undefined,
							action.payload._at,
						)
					) {
						// NOTE: we are changing the structure here to be slightly more sensible (TODO do this on the backend rather)
						let observation = {
							...action.payload.recording,
							derived_files: action.payload.derived_files,
							_at: action.payload._at,
						};
						state.byId[action.payload.recording.id] = observation;
						state.byId[action.payload.recording.id]._loadStatus = "idle";
					}
				}
			})
			.addCase(fetchObservationById.rejected, (state, action) => {
				if (!state.byId[action.meta.arg]) {
					state.byId[action.meta.arg] = {};
				}
				state.byId[action.meta.arg]._loadStatus = "rejected";
				state.byId[action.meta.arg]._errorMessage += action.payload;
			})
			.addCase(fetchObservationsByIds.pending, (state, action) => {
				if (action.meta.arg || action.meta.arg.length > 0) {
					action.meta.arg.map((id) => {
						if (!state.byId[id]) {
							state.byId[id] = {};
						}
						state.byId[id]._loadStatus = "loading";
					});
				}
			})
			.addCase(fetchObservationsByIds.fulfilled, (state, action) => {
				if (action?.payload?.length > 0) {
					unChunkDataArray(action.payload).map((obs) => {
						// TODO check if this is an older or newer observation
						state.byId[obs.id] = obs;
						state.byId[obs.id]._loadStatus = "idle";
					});
				}
			})
			.addCase(toggleTag.pending, (state, action) => {
				if (!state.tagApplicationByObservationId[action.meta.arg]) {
					state.tagApplicationByObservationId[action.meta.arg?.observationId] =
						{};
				}
				state.tagApplicationByObservationId[
					action.meta.arg?.observationId
				]._loadStatus = "loading";
			})
			.addCase(toggleTag.fulfilled, (state, action) => {
				// If we have a payload, tag was added
				if (action.payload?.recording?.id) {
					let observation = {
						...action.payload.recording,
						derived_files: action.payload.derived_files,
						_at: action.payload._at,
					};
					state.byId[action.payload.recording.id] = observation;

					state.tagApplicationByObservationId[
						action.payload?.recording?.id
					]._loadStatus = "idle";
				}

				// If we have no payload, but tag info, tag was removed - optimistically
				// remove from observation in state
				if (!action.payload && action.meta?.arg?.observationId && action.meta?.arg?.name) {
					const obsId = action.meta?.arg?.observationId
					const tagName = action.meta?.arg?.name
					const obs = state.byId[obsId]
					if (obs) {
						state.byId[obsId] = { ...obs, tags: obs.tags.filter(t => t.name != tagName) }

					}
				}
			})
			.addCase(toggleTag.rejected, (state, action) => {
				// TODO verify this
				if (!state.byId[action.meta.arg]) {
					state.tagApplicationByObservationId[action.meta.arg?.observationId] =
						{};
				}
				state.tagApplicationByObservationId[
					action.meta.arg?.observationId
				]._loadStatus = "rejected";
				state.tagApplicationByObservationId[
					action.meta.arg?.observationId
				]._errorMessage += action.payload;
			})
			.addCase(observationAcceptance.pending, (state, action) => {
				if (!state.acceptanceByObservationId[action.meta.arg]) {
					state.acceptanceByObservationId[action.meta.arg?.observationId] = {};
				}
				state.acceptanceByObservationId[
					action.meta.arg?.observationId
				]._loadStatus = "loading";
			})
			.addCase(observationAcceptance.fulfilled, (state, action) => {
				if (action.payload?.observation?.id) {
					state.byId[action.payload?.observation?.id].accepted =
						action.payload?.observation?.accepted;
					state.byId[action.payload?.observation?.id].selected_track = action
						.payload?.observation?.accepted
						? action.payload?.observation?.selected_track
						: null;

					// update the etag
					state.byId[action.payload?.observation?.id]._etag =
						action.payload?.observation?._etag;

					state.acceptanceByObservationId[
						action.payload?.observation?.id
					]._loadStatus = "idle";
				}
			})
			.addCase(observationAcceptance.rejected, (state, action) => {
				state.acceptanceByObservationId[
					action.meta.arg?.observationId
				]._loadStatus = "rejected";
			})
			.addCase(fetchObservationIDs.pending, (state) => {
				state.ids._loadStatus = "loading";
			})
			.addCase(fetchObservationIDs.fulfilled, (state, action) => {
				state.ids._loadStatus = "idle";
				if (allowUpdate(state.ids.at, action.payload.at)) {
					state.ids.at = action.payload.at;
					state.ids.ids = action?.payload?.data;
				}
			})
			.addCase(fetchObservationIDs.rejected, (state) => {
				state.ids._loadStatus = "rejected";
			})
			.addCase(archiveObservationByID.pending, (state) => {
				if (!state["archiving"]) {
					state["archiving"] = {};
				}
				state["archiving"]._saveStatus = "updating";
			})
			.addCase(archiveObservationByID.fulfilled, (state, action) => {
				state.byId[action.meta.arg].deleted = true;
				state["archiving"]._saveStatus = "success";
			})
			.addCase(archiveObservationByID.rejected, (state, action) => {
				state["archiving"]._saveStatus = "rejected";
				state["archiving"]._errorMessage += action.payload;
			});
	},
});

// Selector for all the pipeline names
export const selectPipelines = (state) => state.observations.pipelines;

// Selector for all the solver statuses
export const selectSolverStatuses = (state) =>
	state.observations.solverStatuses;

// Selector for all the solver statuses
export const selectTaskTypes = (state) => state.observations.taskTypes;

// Selector for all observations
export const selectObservationsById = (state) => state.observations.byId;

// Selector for all observations
export const selectTagApplicationByObservationId = (state) =>
	state.observations.tagApplicationByObservationId;

// select all the possible tag values
export const selectTagDescriptionsByName = (state) =>
	state.observations.tagDescriptions;

// selector for the picking of data sets by observation
export const selectAcceptanceByObservationId = (state) =>
	state.observations.acceptanceByObservationId;

export const selectArchivingObservation = (state) =>
	state.observations.archiving;

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectObservationsByContext(state, `rso/${id}`))
 */
export const selectObservationsByContext = createSelector(
	[
		// first input - extract value from `state`
		(state) => state.observations.matchesByCtx,
		// Take the second arg, `context`, and forward to the output selector
		(state, context) => context,
	],
	// Output selector gets (`observations, context)` as args
	(matchesByCtx, context) => {
		return matchesByCtx[context];
	},
);

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectObservationsByIds(state, ["1", "2"]))
 */
export const selectObservationsByIds = createSelector(
	[
		// first input - extract value from `state`
		selectObservationsById,
		// Take the second arg, `ids`, and forward to the output selector
		(state, ids) => ids,
	],
	// Output selector gets (`byId, ids)` as args
	(byId, ids) => {
		return ids.map((id) => byId[id] || { id });
	},
);

/** selectObservationIDs returns the list of all observation IDs that match a given
 * filter, without pagination support. This use-case is distinc from `selectObservationsByContext`
 * in that it does not contain observation data (only IDs) and is used for arbitrary navigation
 * (stepping through) observation lists matching an arbitrary filter.
 * Returns: { ids: [], _loadStatus: "..."}
 */
export const selectObservationIDs = (state) => state.observations.ids;

/** selectLocationWithinIDs returns a "locator" or "cursor" object that, given
 * an observation ID, indicates where it positionally exists within the collection
 * of all IDs matching the current filter criteria, as well as optionally the IDs
 * of the previous and next observation, and finally the total collection size.
 * As a convenience, the _loadStatus of `ids` are also returned to allow a view to
 * only render once resolved.
 * { pos: 0, count: 17, prevID: '', nextID: '', _loadStatus: "idle"   }
 */
export const selectLocationWithinIDs = createSelector(
	[selectObservationIDs, (state, observationID) => observationID],
	({ ids, _loadStatus }, id) => {
		if (!ids || !ids.length || !id) {
			return { pos: -1, count: 0, _loadStatus };
		}
		const pos = ids.indexOf(id);
		const count = ids.length;
		if (pos == -1) {
			return { pos, count, _loadStatus };
		}
		const prevID = pos > 0 ? ids[pos - 1] : null;
		const nextID = pos < ids.length - 1 ? ids[pos + 1] : null;
		return { pos, count, prevID, nextID, _loadStatus };
	},
);

/** selectUnfetchedPeers returns an ordered list of observation IDs
 * which would be appropriate to fetch from the back-end if a user
 * were to currently be browsing a particular observation.
 * This powers optimistic pre-fetching to allow for a smooth
 * navigation experience as the user steps through the "current list"
 * of filtered observations.
 * The returned IDs are returned in the order of suggested fetch order,
 * based on likelihood of user navigation.
 * [ "id1", "id2", "id3" ]
 */
export const selectUnfetchedPeers = createSelector(
	[
		selectObservationIDs,
		selectObservationsById,
		(state, observationID) => observationID,
	],
	({ ids, _loadStatus }, byId, id) => {
		if (_loadStatus == "loading" || !ids || !ids.length || !id) {
			return [];
		}
		const pos = ids.indexOf(id);
		if (pos == -1) {
			return [];
		}
		// TODO: MVP implementation, requires a proper synchronised walk
		// "away" from the current item, leapfrogging next then prev.
		const n1 = pos < ids.length - 1 ? ids[pos + 1] : null;
		const p1 = pos > 0 ? ids[pos - 1] : null;
		const n2 = pos < ids.length - 2 ? ids[pos + 2] : null;
		const candidates = [n1, p1, n2];
		return candidates.filter(
			(candidate) =>
				candidate != null &&
				(byId[candidate] == null || byId[candidate]._loadStatus == "rejected"),
		);
	},
);

export const { clearMatches } = observationSlice.actions;

export default observationSlice.reducer;
